/**
 * intakeUpload — direct-to-Supabase signed upload helpers + client-side
 * thumbnail generation for images and videos.
 *
 * Flow per file:
 *   1. POST /api/artist-intake/{token}/upload-url with metadata → {signed_url, token, path, public_url, bucket_name}
 *   2. PUT signed_url with file body
 *   3. Return {storage_path, file_url, bucket_name}
 *
 * The frontend never sees Supabase URL/keys.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Classify a File into our backend file_type enum. */
export function detectFileType(file) {
  const m = (file.type || '').toLowerCase();
  if (m.startsWith('image/')) return 'image';
  if (m.startsWith('video/')) return 'video';
  if (m.startsWith('audio/')) return 'audio';
  if (m === 'application/pdf') return 'pdf';
  return 'other';
}

/** Pretty file size. */
export function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

/**
 * Request a signed upload target from backend.
 */
export async function requestUploadUrl(token, kind, file, temporaryClientId) {
  const body = {
    kind, // 'pfp' | 'work' | 'thumbnail'
    file_name: file.name,
    mime_type: file.type || '',
    file_size: file.size || 0,
    file_type: detectFileType(file),
    temporary_client_id: temporaryClientId,
  };
  const res = await axios.post(`${API}/artist-intake/${token}/upload-url`, body);
  return res.data;
}

/**
 * Upload a single file via PUT to a Supabase signed URL.
 * Reports progress via onProgress(percent 0..100).
 */
export function uploadToSignedUrl(signedUrl, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', signedUrl, true);
    if (file.type) {
      xhr.setRequestHeader('Content-Type', file.type);
    }
    xhr.upload.onprogress = (evt) => {
      if (evt.lengthComputable && onProgress) {
        onProgress(Math.round((evt.loaded / evt.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });
}

/**
 * Full pipeline: request signed url → put file → return metadata.
 */
export async function uploadFileToIntake({ token, kind, file, onProgress, temporaryClientId }) {
  const target = await requestUploadUrl(token, kind, file, temporaryClientId);
  await uploadToSignedUrl(target.signed_url, file, onProgress);
  return {
    bucket_name: target.bucket_name,
    storage_path: target.path,
    file_url: target.public_url,
    file_type: target.file_type,
  };
}

/**
 * Generate a JPEG thumbnail Blob from an image File.
 * Returns Promise<File|null>. Width-capped at maxW px.
 */
export function generateImageThumbnail(file, maxW = 480, quality = 0.7) {
  return new Promise((resolve) => {
    if (!file || !file.type.startsWith('image/')) return resolve(null);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const ratio = Math.min(1, maxW / img.naturalWidth);
        const w = Math.round(img.naturalWidth * ratio);
        const h = Math.round(img.naturalHeight * ratio);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url);
            if (!blob) return resolve(null);
            const thumbFile = new File(
              [blob],
              `thumb-${file.name.replace(/\.[^.]+$/, '')}.jpg`,
              { type: 'image/jpeg' },
            );
            resolve(thumbFile);
          },
          'image/jpeg',
          quality,
        );
      } catch (e) {
        URL.revokeObjectURL(url);
        resolve(null);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

/**
 * Generate a JPEG thumbnail Blob from a video File using a hidden video element.
 * Captures the first painted frame at ~1s (or 0 if shorter).
 */
export function generateVideoThumbnail(file, maxW = 640, quality = 0.7) {
  return new Promise((resolve) => {
    if (!file || !file.type.startsWith('video/')) return resolve(null);
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    let done = false;

    const cleanup = () => {
      try { URL.revokeObjectURL(url); } catch (e) { /* noop */ }
      try { video.remove(); } catch (e) { /* noop */ }
    };

    const grab = () => {
      if (done) return;
      done = true;
      try {
        const ratio = Math.min(1, maxW / (video.videoWidth || maxW));
        const w = Math.round((video.videoWidth || 480) * ratio);
        const h = Math.round((video.videoHeight || 270) * ratio);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            cleanup();
            if (!blob) return resolve(null);
            const thumb = new File(
              [blob],
              `thumb-${file.name.replace(/\.[^.]+$/, '')}.jpg`,
              { type: 'image/jpeg' },
            );
            resolve(thumb);
          },
          'image/jpeg',
          quality,
        );
      } catch (e) {
        cleanup();
        resolve(null);
      }
    };

    video.addEventListener('loadeddata', () => {
      // Seek to 1s for a more representative frame
      const target = Math.min(1, (video.duration || 0) * 0.25);
      try {
        video.currentTime = target;
      } catch (e) {
        grab();
      }
    });
    video.addEventListener('seeked', grab);
    video.addEventListener('error', () => {
      cleanup();
      resolve(null);
    });

    // Hard timeout — never block submit on a stuck thumbnail
    setTimeout(() => {
      if (!done) {
        cleanup();
        resolve(null);
      }
    }, 8000);

    video.src = url;
  });
}

/**
 * Read intrinsic dimensions of an image (sync after load).
 */
export function readImageDims(file) {
  return new Promise((resolve) => {
    if (!file || !file.type.startsWith('image/')) return resolve({});
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(dims);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({});
    };
    img.src = url;
  });
}

/**
 * Read intrinsic video metadata: dimensions + duration.
 */
export function readVideoMeta(file) {
  return new Promise((resolve) => {
    if (!file || !file.type.startsWith('video/')) return resolve({});
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.onloadedmetadata = () => {
      const meta = {
        width: video.videoWidth || null,
        height: video.videoHeight || null,
        duration_seconds: video.duration ? Math.round(video.duration * 100) / 100 : null,
      };
      URL.revokeObjectURL(url);
      resolve(meta);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({});
    };
    video.src = url;
  });
}
